import { useGetSubstitutabilityQuery } from 'store/services/core/substitutability';
import { useGetSimpleUsersByRolesQuery, useGetAllUsersQuery } from 'store/services/core/user';
import { useMemo } from 'react';

import { SUBSTITUTABILITY_LISTING_TYPE, UserStatus } from 'types/enums';
import type { RoleWithTags } from 'types';

interface UseSubstitutabilityUsersParams {
  absentUserId?: string;
  allUserRoles: RoleWithTags[];
}

interface UseSubstitutabilityUsersResult {
  userOptions: Array<{ value: string; label: string }>;
  isLoading: boolean;
  error: unknown;
  substitutabilitySettings: {
    isVisible: boolean;
    listingType: SUBSTITUTABILITY_LISTING_TYPE;
  } | null;
}

export const useSubstitutabilityUsers = ({ absentUserId, allUserRoles }: UseSubstitutabilityUsersParams): UseSubstitutabilityUsersResult => {
  const { data: substitutabilityData, isLoading: isSubstitutabilityLoading, error: substitutabilityError } = useGetSubstitutabilityQuery();

  const allRoleIds = useMemo(() => allUserRoles.map((role) => role.id), [allUserRoles]);

  const shouldFetchAllUsers =
    substitutabilityData?.listingType === SUBSTITUTABILITY_LISTING_TYPE.ALL_USERS ||
    substitutabilityData?.listingType === SUBSTITUTABILITY_LISTING_TYPE.ALL_USER_ROLES;
  const shouldFetchUsersByRoles = substitutabilityData?.listingType === SUBSTITUTABILITY_LISTING_TYPE.ONE_USER_ROLE && allRoleIds.length > 0;

  const {
    data: usersByRolesData,
    isLoading: isUsersByRolesLoading,
    error: usersByRolesError,
  } = useGetSimpleUsersByRolesQuery(
    {
      ids: allRoleIds,
      page: 1,
      pageSize: null,
      orderBy: 'fullName',
      orderDesc: false,
      search: '',
    },
    { skip: !shouldFetchUsersByRoles }
  );

  const {
    data: allUsersData,
    isLoading: isAllUsersLoading,
    error: allUsersError,
  } = useGetAllUsersQuery(
    {
      DateOfDeactivation: '',
      DateOfActivation: '',
      Email: '',
      FullName: '',
      Status: [UserStatus.ACTIVE],
      page: 1,
      pageSize: null,
      orderBy: 'fullName',
      orderDesc: false,
      search: '',
    },
    { skip: !shouldFetchAllUsers }
  );

  const userOptions = useMemo(() => {
    if (!substitutabilityData || !absentUserId) return [];

    let filteredUsers: Array<{ id: string; fullName: string }> = [];

    switch (substitutabilityData.listingType) {
      case SUBSTITUTABILITY_LISTING_TYPE.ALL_USERS:
        filteredUsers = allUsersData?.items?.map((user) => ({ id: user.id, fullName: user.fullName || user.email })) || [];
        break;
      case SUBSTITUTABILITY_LISTING_TYPE.ONE_USER_ROLE:
        filteredUsers = usersByRolesData?.items?.map((user) => ({ id: user.id, fullName: user.fullName || user.email })) || [];
        break;
      case SUBSTITUTABILITY_LISTING_TYPE.ALL_USER_ROLES:
        if (allUsersData?.items && allUserRoles.length > 0) {
          filteredUsers = allUsersData.items
            .filter((user) => {
              const userRoleIds = user.userRoles.map((role) => role.roleId);
              return allRoleIds.every((roleId) => userRoleIds.includes(roleId));
            })
            .map((user) => ({ id: user.id, fullName: user.fullName || user.email }));
        }
        break;
      case SUBSTITUTABILITY_LISTING_TYPE.NONE:
      default:
        return [];
    }

    filteredUsers = filteredUsers.filter((user) => user.id !== absentUserId);

    return filteredUsers.map((user) => ({
      value: user.id,
      label: user.fullName,
    }));
  }, [substitutabilityData, absentUserId, allUsersData?.items, usersByRolesData?.items, allUserRoles.length, allRoleIds]);

  const isLoading = isSubstitutabilityLoading || isUsersByRolesLoading || isAllUsersLoading;
  const error = substitutabilityError || usersByRolesError || allUsersError;

  return {
    userOptions,
    isLoading,
    error,
    substitutabilitySettings: substitutabilityData
      ? {
          isVisible: substitutabilityData.isVisible,
          listingType: substitutabilityData.listingType,
        }
      : null,
  };
};
